<template>
  <FishScene cena="vila" :escurecimento="0.3" class="vila">
    <div class="vila__interior">
      <h1 class="t-titulo-tela vila__titulo">{{ $t('shop.village.title') }}</h1>

      <!--
        5.6 — A TELA DA VILA FICA (D16). O cenário dela mudou duas vezes, e as duas lições ficam:

        1. A primeira versão trocou o `village-bg.png` por uma cena SUBMARINA do pack — uma vila
           na superfície mostrada debaixo d'água (com minas navais ao fundo!) perdeu todo o calor.
        2. O PNG da vila voltou, mas era um JPEG 16:9 em `cover` numa página de altura inteira: no
           telefone ele ampliava até deformar ("está muito esticada", o autor, 03/09/2026).

        Agora a vila é uma cena de SUPERFÍCIE em parallax — a enseada com nuvens do pack de céu
        (`cena-vila`, Ocean_3), nativa 576×324 e escalada inteira como toda tela do jogo. As placas
        de madeira e os lojistas continuam sendo o que diz "vila"; o fundo só diz "praia, dia".
      -->
      <ul class="vila__lojas">
        <li v-for="(loja, i) in lojas" :key="loja.id">
          <button
            class="vila__loja"
            type="button"
            :style="{ '--ordem': i }"
            @click="entrar(loja)"
          >
            <span class="vila__placa repouso-balanca" :style="{ animationDelay: `${i * -1.7}s` }">
              <FishIcon :nome="loja.icone" tamanho="grande" />
              <span class="vila__nome">{{ $t(`shop.village.shops.${loja.type}.name`) }}</span>
            </span>
            <img class="vila__lojista" :src="loja.retrato" alt="" />
          </button>
        </li>
      </ul>
    </div>

    <!--
      5.1 e 5.2 — DENTRO DA LOJA: meia tela de lugar, meia de painel (D16).

      Antes: o Velho João era um quadradinho no canto superior esquerdo de um `shop-interior.png`
      de 3,5 MB em tela cheia — o personagem era a menor coisa da tela e o fundo a maior. E a
      caixa de diálogo era uma barra atravessando o topo inteiro para caber uma frase curta.
    -->
    <FishModal :aberto="!!lojaAtual" :rotulo="tituloDaLoja" tom="madeira" largura="largo" @fechar="sair">
      <template #titulo>
        <span>{{ tituloDaLoja }}</span>
      </template>

      <NpcStage
        v-if="lojaAtual"
        :retrato="lojaAtual.retrato"
        :nome="$t(`shop.village.shops.${lojaAtual.type}.keeper`)"
        :cenario="interior"
        :fala="fala"
        @interact="conversar"
      >
        <component :is="lojaAtual.componente" @interact="conversar" @recado="dizer" />
      </NpcStage>
    </FishModal>
  </FishScene>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import RodShop from '../shared/shop/RodShop.vue';
import BaitShop from '../shared/shop/BaitShop.vue';
import BoatShop from '../shared/shop/BoatShop.vue';
import FishMarket from '../shared/shop/FishMarket.vue';
import PremiumShop from '../shared/shop/PremiumShop.vue';
import HouseShop from '../shared/shop/HouseShop.vue';
import { FishModal, FishIcon, NpcStage, FishScene } from '@/shared/ui';
import { saudacaoDoLojista } from '../shared/shop/falas';

/**
 * A vila e as seis lojas (Fase 5, D16).
 *
 * ## Por que a loja de hoje era feia — e não era gosto, era estrutura
 *
 * - **O NPC era uma miniatura** na frente de um fundo em tela cheia: o personagem era a menor
 *   coisa da tela e o fundo a maior, exatamente ao contrário do que deveria;
 * - **a caixa de diálogo era uma barra vazia** atravessando o topo para caber uma frase curta —
 *   noventa por cento de espaço morto;
 * - **os itens eram um paredão de cards**, catorze de uma vez.
 *
 * Os três viram: `NpcStage` (o lojista em tamanho de gente, com balão sobre ele) e `LojaPainel`
 * (abas, card de detalhe, grade de encaixes), que cada sub-loja usa.
 *
 * ## O ícone de cada loja
 *
 * Do pacote de pesca (D20), não do Font Awesome. `fa-bug` para a casa de iscas era um **inseto de
 * interface**; agora é a lata de isca, que é o objeto de que a loja trata.
 */
const LOJAS = {
  rod: { componente: RodShop, icone: 'nav-pescar', retrato: require('@/assets/images/shopkeepers/old-john.png') },
  bait: { componente: BaitShop, icone: 'cais-isca', retrato: require('@/assets/images/shopkeepers/maria.png') },
  boat: { componente: BoatShop, icone: 'ship', retrato: require('@/assets/images/shopkeepers/captain.png') },
  market: { componente: FishMarket, icone: 'nav-loja', retrato: require('@/assets/images/shopkeepers/merchant.png') },
  premium: { componente: PremiumShop, icone: 'gem', retrato: require('@/assets/images/shopkeepers/premium-merchant.png') },
  house: { componente: HouseShop, icone: 'door-open', retrato: require('@/assets/images/shopkeepers/house-seller.png') },
};

export default defineComponent({
  name: 'FishingVillage',
  components: { FishModal, FishIcon, NpcStage, FishScene },
  data() {
    return {
      lojaAtual: null,
      fala: '',
      interior: require('@/assets/images/shop-interior.png'),
    };
  },
  computed: {
    lojas() {
      return Object.entries(LOJAS).map(([type, config], i) => ({
        id: i + 1,
        type,
        componente: markRaw(config.componente),
        icone: config.icone,
        retrato: config.retrato,
      }));
    },
    tituloDaLoja() {
      return this.lojaAtual ? this.$t(`shop.village.shops.${this.lojaAtual.type}.name`) : '';
    },
  },
  methods: {
    entrar(loja) {
      this.lojaAtual = loja;
      this.fala = this.saudacao(loja.type);
    },
    sair() {
      this.lojaAtual = null;
      this.fala = '';
    },
    conversar() {
      this.fala = this.saudacao(this.lojaAtual.type);
    },
    /**
     * O balão é **reativo** (5.2): a sub-loja avisa o que aconteceu e o NPC comenta.
     *
     * "O diálogo deixa de ser barra e vira balão sobre o NPC — curto, e reativo: ele comenta o que
     * você selecionou, a compra, e o fato de faltar ouro. É assim que o NPC ganha o lugar dele."
     */
    dizer(recado) {
      if (recado) this.fala = recado;
    },
    /**
     * Sortear a saudação **exige o `tm()`**, não o `t()`: em modo Composition (`legacy: false`) o
     * `t()` devolve a chave de volta quando o valor é um array, então `Array.isArray` era sempre
     * falso e os seis lojistas diziam a mesma frase de reserva — as saudações estavam no JSON e
     * nunca chegaram à tela. O sorteio mora no `falas.js` porque as sub-lojas precisam dele também.
     */
    saudacao(tipo) {
      return saudacaoDoLojista(this, tipo);
    },
  },
});
</script>

<style scoped>
/* O cenário e o véu são do `FishScene` (escurecimento 0,3: a enseada aparece, o título e as
 * placas continuam legíveis nas pontas — o véu dele já é mais escuro em cima e embaixo). */
.vila {
  min-height: 100vh;
}

.vila__interior {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  /* Reserva a folha ativa do rodapé e a faixa com o nome — com 64px o fim da página ficava atrás
   * delas no telefone (mesmo defeito do Upgrade e do Inventário, 23/09). */
  padding: var(--e-5) var(--e-4) calc(var(--altura-barra) + var(--e-5));
}

.vila__titulo {
  text-align: center;
  color: var(--papel-luz);
  /* O contorno duro prega o título sobre o cenário. Sem ele, texto claro sobre água clara some —
   * e o `FishScene` tem partes claras. */
  text-shadow: 2px 2px 0 var(--agua-abissal), -2px 2px 0 var(--agua-abissal),
    2px -2px 0 var(--agua-abissal), -2px -2px 0 var(--agua-abissal);
  margin-bottom: var(--e-5);
}

.vila__lojas {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--e-4);
}

@media (min-width: 600px) {
  .vila__lojas {
    grid-template-columns: repeat(3, 1fr);
  }
}

.vila__loja {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-height: 180px;
  padding: 0;
  animation: subir-surgindo var(--t-medio) var(--curva-entrada) both;
  animation-delay: calc(min(var(--ordem, 0), 8) * 60ms);
}

/**
 * A placa da loja: madeira pendurada, e ela **balança devagar** (D10-3).
 *
 * "Nada perfeitamente parado." Uma placa pendurada que não se move é a assinatura de aplicativo; a
 * mesma placa balançando um grau e meio é uma vila. Cada uma tem um `animation-delay` negativo
 * diferente, senão as seis balançam em bloco e viram um carrossel.
 */
.vila__placa {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  width: 100%;
  padding: var(--e-2);

  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/botao-neutro.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background: var(--madeira);
  background-clip: padding-box;
  image-rendering: pixelated;

  transform-origin: top center;
}

.vila__nome {
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  line-height: var(--lh-curto);
  color: var(--papel-luz);
  text-align: center;
}

/* O lojista aparece embaixo da placa: é o que diz que há alguém ali dentro. */
.vila__lojista {
  width: auto;
  height: 96px;
  object-fit: contain;
  object-position: bottom;
  image-rendering: pixelated;
  filter: drop-shadow(0 3px 0 rgba(0, 0, 0, 0.45));
  transition: transform var(--t-rapido) var(--curva-quique);
}

@media (hover: hover) {
  .vila__loja:hover .vila__lojista {
    transform: translateY(-4px);
  }
}

.vila__loja:active .vila__lojista {
  transform: translateY(2px);
}
</style>
